import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { Globe, Check } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { LANGUAGES } from '../i18n';
import api from '../lib/api';

// Apply dir + lang attributes to <html> whenever language changes
export function applyLanguageDirection(code) {
  const lang = LANGUAGES.find(l => l.code === code) || LANGUAGES[0];
  document.documentElement.lang = lang.code;
  document.documentElement.dir = lang.dir === 'rtl' ? 'rtl' : 'ltr';
}

export default function LanguageSwitcher({ variant = 'icon' }) {
  const { i18n, t } = useTranslation();
  const current = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES.find(l => l.code === i18n.language?.split('-')[0]) || LANGUAGES[0];

  useEffect(() => {
    applyLanguageDirection(current.code);
  }, [current.code]);

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    applyLanguageDirection(code);
    // Persist to server for signed-in users so message translation targets their choice
    const token = typeof window !== 'undefined' ? localStorage.getItem('sd_token') : null;
    if (token) {
      api.put('/me/language', { preferred_language: code }).catch(() => {});
    }
  };

  const trigger = variant === 'full' ? (
    <button
      data-testid="language-switcher-full"
      className="flex items-center gap-2 px-3 py-2 text-xs rounded-lg border border-amber-100 hover:border-amber-300 hover:bg-amber-50 transition-all"
    >
      <Globe size={14} className="text-amber-500" />
      <span className="font-medium text-obsidian">{current.name}</span>
    </button>
  ) : (
    <button
      data-testid="language-switcher"
      aria-label={t('common.language')}
      className="p-1.5 rounded-lg hover:bg-amber-50 transition-colors flex items-center gap-1.5"
    >
      <Globe size={16} className="text-obsidian/70" />
      <span className="hidden sm:inline text-[11px] uppercase tracking-[0.15em] text-obsidian/60 font-medium">
        {current.code}
      </span>
    </button>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 max-h-80 overflow-y-auto rounded-lg shadow-xl border-amber-100/50" data-testid="language-switcher-menu">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-amber-600/70 font-semibold">
          {t('common.language')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {LANGUAGES.map(lang => {
          const isActive = lang.code === current.code;
          return (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              data-testid={`lang-option-${lang.code}`}
              className={`text-xs flex items-center justify-between gap-2 ${isActive ? 'bg-amber-50/70' : ''}`}
            >
              <span className="flex items-center gap-2">
                <span className="text-[10px] tracking-wider font-mono text-amber-600 w-6">{lang.code.toUpperCase()}</span>
                <span>{lang.name}</span>
              </span>
              {isActive && <Check size={12} className="text-amber-500" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
